import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import ChargingScheduleForm from './ChargingScheduleForm'
import { ChargingScheduleFormType, RepeatOptions, ScheduleCounter } from 'src/Libs/e-mobility/vehicle-integration'
import { createChargingSchedule, deleteChargingSchedule, updateChargingSchedule } from '../../api/charging-schedule.api'
import { ChargingScheduleModel } from '../../models/charging-schedule.model'
import { combineDateAndTime, createCron } from '../../functions'
import { useQueryClient } from '@tanstack/react-query'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog/ActionDialog'
import { PositionedAction } from '@e3dc-react/shell/Elements/Dialog/ActionDialog/types'
import { useMediaQuery, useTheme } from '@mui/system'
import { useTargetSoc, useValidateChargingScheduleForm } from '../../hooks'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'
import { setTimeZoneKeepLocalTime } from '@e3dc-react/shell/Libs/date'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

export interface VehicleChargingScheduleModalProps {
  onClose?: () => void
  place?: number
  scheduleId?: number
  vehicleId: number
  installationId: number
  chargingScheduleData: ChargingScheduleFormType
  scheduleCounter?: ScheduleCounter
  temporaryScheduleName?: string
  isEditing: boolean
  chargeLimitMax?: number
  isDialogOpen: boolean
}

/**
 *  Modal to set charging schedule
 */
const VehicleChargingScheduleModal: React.FC<VehicleChargingScheduleModalProps> = props => {
  const {
    vehicleId,
    installationId,
    chargingScheduleData,
    scheduleId,
    scheduleCounter,
    temporaryScheduleName,
    onClose,
    isEditing,
    chargeLimitMax,
    isDialogOpen,
  } = props
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()
  const queryKey = ['vehicle-integration', 'charging-schedules', vehicleId]
  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const { targetSoc: minStateOfCharge } = useTargetSoc(Number(vehicleId), Number(installationId))
  const { installation } = useActiveInstallationContext()
  const { chargingScheduleFormSchema } = useValidateChargingScheduleForm(isEditing)

  const form = useForm<ChargingScheduleFormType>({
    resolver: zodResolver(chargingScheduleFormSchema),
    defaultValues: chargingScheduleData,
    mode: 'onSubmit',
  })

  const counter = scheduleCounter ?? {
    weekly: 0,
    daily: 0,
    oneTime: 0,
  }

  const handleOnSubmit = async (data: ChargingScheduleFormType): Promise<void> => {
    const { scheduleName, targetStateOfCharge, startDate, startTime, endDate, enabled, repeat, selectedWeekDays } = data
    const departureTime = combineDateAndTime(startDate, startTime, installation?.timezone)!
    const newSchedule: Omit<ChargingScheduleModel, 'id'> = {
      name: scheduleName ?? null,
      targetStateOfCharge: targetStateOfCharge as number,
      schedule: { departureTime },
    }

    if (repeat !== RepeatOptions.doNotRepeat) {
      const cron = createCron(selectedWeekDays, startTime as Date, repeat)
      newSchedule.schedule = {
        enabled,
        startDate: combineDateAndTime(startDate, startTime, installation?.timezone)!,
        endDate: setTimeZoneKeepLocalTime(endDate as Date),
        cron,
      }
    }

    let requestError = false
    if (scheduleId) {
      const { hasErrors } = await updateChargingSchedule(vehicleId, installationId, scheduleId, newSchedule, authFetch)
      requestError = hasErrors
    } else {
      const { hasErrors } = await createChargingSchedule(vehicleId, installationId, newSchedule, authFetch)
      requestError = hasErrors
    }

    if (requestError) {
      enqueueErrorStack(t('emobility.vehicle-integration.charging-schedule.create-schedule-failure-message'))
      return
    }

    enqueueSuccessStack(t('emobility.vehicle-integration.charging-schedule.create-schedule-success-message'))
    queryClient.invalidateQueries({ queryKey })
    onClose?.()
  }

  const handleOnDelete = async (): Promise<void> => {
    if (scheduleId) {
      const { hasErrors } = await deleteChargingSchedule(vehicleId, installationId, scheduleId, authFetch)

      if (hasErrors) {
        enqueueErrorStack(t('emobility.vehicle-integration.charging-schedule.delete-schedule-failure-message'))
        return
      }
      enqueueSuccessStack(t('emobility.vehicle-integration.charging-schedule.delete-schedule-success-message'))
    }
    queryClient.invalidateQueries({ queryKey })
    onClose?.()
  }

  const secondaryAction: PositionedAction = {
    position: '3',
    style: 'secondary',
    name: t('common.cancel'),
    onClick: () => onDialogClose(),
  }

  const primaryAction: PositionedAction = {
    position: '4',
    style: 'primary',
    name: t('common.save'),
    onClick: form.handleSubmit(handleOnSubmit),
  }

  const onDialogClose = (): void => {
    onClose?.()
  }

  return (
    <Dialog
      open={isDialogOpen}
      fullScreen={!upMd}
      navigateBackButton={true}
      closeButtonVisible={false}
      onClose={onDialogClose}
      onNavigateBack={onDialogClose}
      title={t('emobility.vehicle-integration.charging-schedule.charging-schedule-dialog-header')}
      actions={[primaryAction, secondaryAction]}
      maxWidth={'sm'}
    >
      <FormProvider {...form}>
        <ChargingScheduleForm
          handleOnDelete={handleOnDelete}
          minStateOfCharge={minStateOfCharge}
          scheduleCounter={counter}
          temporaryScheduleName={temporaryScheduleName}
          isEditing={isEditing}
          chargeLimitMax={chargeLimitMax}
        />
      </FormProvider>
    </Dialog>
  )
}

export default VehicleChargingScheduleModal
